// Utilidades para formateo de datos en Clientelo

export const formatearMoneda = (valor: number, moneda: string = 'COP'): string => {
  if (valor === null || valor === undefined) return '$0';
  
  const formatter = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  
  return formatter.format(valor);
};

export const formatearPorcentaje = (valor: number): string => {
  if (valor === null || valor === undefined) return '0%';
  
  return `${(valor * 100).toFixed(1)}%`;
};

export const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return 'Fecha no disponible';
  
  try {
    const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
    
    // Check if date is valid
    if (isNaN(fechaObj.getTime())) {
      return 'Fecha inválida';
    }
    
    return fechaObj.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    return 'Fecha inválida';
  }
};

export const formatearFechaCompleta = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return 'Fecha no disponible';
  
  try {
    const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
    
    // Check if date is valid
    if (isNaN(fechaObj.getTime())) {
      return 'Fecha inválida';
    }
    
    return fechaObj.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Fecha inválida';
  }
};

// New: Format date with time
export const formatearFechaConHora = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return 'Fecha no disponible';
  
  try {
    const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
    
    if (isNaN(fechaObj.getTime())) {
      return 'Fecha inválida';
    }
    
    return fechaObj.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Fecha inválida';
  }
};

// New: Format short date (DD/MM/YYYY)
export const formatearFechaCorta = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  
  try {
    const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
    
    if (isNaN(fechaObj.getTime())) {
      return '-';
    }
    
    return fechaObj.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    return '-';
  }
};

export const formatearTelefono = (telefono: string): string => {
  if (!telefono) return '';
  
  // Remover todos los caracteres no numéricos
  const numeros = telefono.replace(/\D/g, '');
  
  // Formatear según el país (ejemplo para Colombia)
  if (numeros.startsWith('57')) {
    return `+57 ${numeros.slice(2, 5)} ${numeros.slice(5, 8)} ${numeros.slice(8)}`;
  }
  
  return telefono;
};

export const formatearSKU = (sku: string): string => {
  if (!sku) return '';
  
  return sku.toUpperCase().replace(/\s+/g, '-');
};

export const formatearNombre = (nombre: string): string => {
  if (!nombre) return '';
  
  return nombre
    .toLowerCase()
    .split(' ')
    .map(palabra => palabra.charAt(0).toUpperCase() + palabra.slice(1))
    .join(' ');
};

export const formatearDireccion = (direccion: string, ciudad: string, pais: string): string => {
  const partes = [direccion, ciudad, pais].filter(Boolean);
  return partes.join(', ');
};

export const formatearNumeroFactura = (numero: string, pais: string): string => {
  const prefijos = {
    'CO': 'FAC',
    'MX': 'CFDI',
    'AR': 'FAC',
    'PE': 'FAC',
    'CL': 'FAC'
  };
  
  const prefijo = prefijos[pais as keyof typeof prefijos] || 'FAC';
  return `${prefijo}-${numero.padStart(6, '0')}`;
};

export const formatearTiempoTranscurrido = (fecha: Date | string): string => {
  if (!fecha) return '';
  
  const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
  const ahora = new Date();
  const diferencia = ahora.getTime() - fechaObj.getTime();
  
  const segundos = Math.floor(diferencia / 1000);
  const minutos = Math.floor(segundos / 60);
  const horas = Math.floor(minutos / 60);
  const dias = Math.floor(horas / 24);
  const meses = Math.floor(dias / 30);
  const años = Math.floor(meses / 12);
  
  if (años > 0) return `hace ${años} año${años > 1 ? 's' : ''}`;
  if (meses > 0) return `hace ${meses} mes${meses > 1 ? 'es' : ''}`;
  if (dias > 0) return `hace ${dias} día${dias > 1 ? 's' : ''}`;
  if (horas > 0) return `hace ${horas} hora${horas > 1 ? 's' : ''}`;
  if (minutos > 0) return `hace ${minutos} minuto${minutos > 1 ? 's' : ''}`;
  
  return 'hace un momento';
};

/** Última compra en el tono del Design System: hoy, ayer, hace 2 días, hace 3 sem. */
export const formatearUltimoPedido = (
  fecha: Date | string | null | undefined,
  ahora: Date = new Date()
): string => {
  if (!fecha) return 'Nunca';
  const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (!(fechaObj instanceof Date) || Number.isNaN(fechaObj.getTime())) return 'Nunca';

  const startHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const startFecha = new Date(fechaObj.getFullYear(), fechaObj.getMonth(), fechaObj.getDate());
  const dias = Math.round((startHoy.getTime() - startFecha.getTime()) / 86400000);

  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 7) return `hace ${dias} días`;
  if (dias < 30) {
    const semanas = Math.max(1, Math.floor(dias / 7));
    return `hace ${semanas} sem.`;
  }
  const meses = Math.max(1, Math.floor(dias / 30));
  if (meses < 12) return `hace ${meses} mes${meses === 1 ? '' : 'es'}`;
  const anios = Math.max(1, Math.floor(meses / 12));
  return `hace ${anios} año${anios === 1 ? '' : 's'}`;
};

export const inicialesNombre = (nombre: string): string => {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  const primero = partes[0];
  if (!primero) return '?';
  const segundo = partes[1];
  if (!segundo) return primero.slice(0, 2).toUpperCase();
  return `${primero.charAt(0)}${segundo.charAt(0)}`.toUpperCase();
};

export const esFechaEnUltimosDias = (
  fecha: Date | string | null | undefined,
  dias: number,
  ahora: Date = new Date()
): boolean => {
  if (!fecha) return false;
  const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (!(fechaObj instanceof Date) || Number.isNaN(fechaObj.getTime())) return false;
  const corte = new Date(ahora);
  corte.setDate(corte.getDate() - dias);
  corte.setHours(0, 0, 0, 0);
  return fechaObj >= corte;
};

export const formatearTamañoArchivo = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const tamaños = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + tamaños[i];
};

export const formatearRiesgoChurn = (riesgo: string): string => {
  const etiquetas = {
    'BAJO': 'Bajo',
    'MEDIO': 'Medio',
    'ALTO': 'Alto'
  };
  
  return etiquetas[riesgo as keyof typeof etiquetas] || riesgo;
};

export const formatearSegmentoCliente = (segmento: string): string => {
  const etiquetas = {
    'VIP': 'VIP',
    'REGULAR': 'Regular',
    'OCASIONAL': 'Ocasional',
    'EN_RIESGO': 'En Riesgo',
    'REACTIVADO': 'Reactivado'
  };
  
  return etiquetas[segmento as keyof typeof etiquetas] || segmento;
};

export const formatearEstadoProducto = (estado: string): string => {
  const etiquetas = {
    'ACTIVO': 'Activo',
    'DESCONTINUADO': 'Descontinuado',
    'AGOTADO': 'Agotado',
    'ARCHIVADO': 'Archivado'
  };
  
  return etiquetas[estado as keyof typeof etiquetas] || estado;
};

export const formatearEstadoVenta = (estado: string): string => {
  const etiquetas = {
    'PENDIENTE': 'Pendiente',
    'PAGADA': 'Pagada',
    'ANULADA': 'Anulada',
    'DEVUELTA': 'Devuelta'
  };
  
  return etiquetas[estado as keyof typeof etiquetas] || estado;
};






